"use client";

import type { EditorBackend } from "./types";

/**
 * Production backend: thin wrappers around the existing Server Actions.
 *
 * The actions are imported **lazily** (dynamic `import()` inside each method)
 * rather than at module top-level. Both action modules carry `"use server"`,
 * so in the Next.js client bundle these dynamic imports resolve to RPC stubs —
 * behavior is identical to calling the actions directly.
 *
 * Why lazy: this module is the default value of `EditorBackendContext`, so it
 * sits in the import graph of every editor component. A static top-level import
 * of the action modules would drag their `server-only` transitive deps into the
 * client/test graph (unit tests evaluate the real modules, which throws). Lazy
 * importing defers that to call-time, where tests either don't invoke the
 * method or mock the action module.
 */
export const realEditorBackend: EditorBackend = {
    isDemo: false,
    assistantStreamEndpoint: "/api/assistant/stream",

    async saveChapterContent(chapterId, content, wordCount, options) {
        const { saveChapterContent } = await import(
            "@/lib/actions/chapter-actions"
        );
        return saveChapterContent(chapterId, content, wordCount, options);
    },

    async createChapter(storyId, title, volumeId) {
        const { createChapter } = await import("@/lib/actions/chapter-actions");
        return createChapter(storyId, title, volumeId);
    },

    async createStoryVolume(storyId, title) {
        const { createStoryVolume } = await import("@/lib/actions/story-outline-actions");
        return createStoryVolume(storyId, title);
    },

    async renameStoryVolume(storyId, volumeId, title) {
        const { renameStoryVolume } = await import("@/lib/actions/story-outline-actions");
        return renameStoryVolume(storyId, volumeId, title);
    },

    async deleteStoryVolume(storyId, volumeId) {
        const { deleteStoryVolume } = await import("@/lib/actions/story-outline-actions");
        return deleteStoryVolume(storyId, volumeId);
    },

    async deleteVolumeChapters(storyId, volumeId, selectedChapterIds) {
        const { deleteVolumeChapters } = await import("@/lib/actions/story-outline-actions");
        return deleteVolumeChapters(storyId, volumeId, selectedChapterIds);
    },

    async updateStoryOutline(input) {
        const { updateStoryOutline } = await import("@/lib/actions/story-outline-actions");
        return updateStoryOutline(input);
    },

    async updateStoryTitle(storyId, title) {
        const { updateStoryTitle } = await import("@/lib/actions/story-actions");
        return updateStoryTitle(storyId, title);
    },

    async updateChapterMeta(chapterId, updates) {
        const { updateChapterMeta } = await import(
            "@/lib/actions/chapter-actions"
        );
        return updateChapterMeta(chapterId, updates);
    },

    async deleteChapter(chapterId) {
        const { deleteChapter } = await import("@/lib/actions/chapter-actions");
        return deleteChapter(chapterId);
    },

    async reorderChapters(storyId, orderedIds) {
        const { reorderChapters } = await import(
            "@/lib/actions/chapter-actions"
        );
        return reorderChapters(storyId, orderedIds);
    },

    async syncChapterMentions(chapterId, entryIds) {
        const { syncChapterMentions } = await import(
            "@/lib/actions/wiki-editor-actions"
        );
        return syncChapterMentions(chapterId, entryIds);
    },

    async searchWikiEntries(storyId, query) {
        const { searchWikiEntries } = await import(
            "@/lib/actions/wiki-editor-actions"
        );
        return searchWikiEntries(storyId, query);
    },

    async uploadChapterImage(storyId, file) {
        const { uploadChapterImage } = await import(
            "@/lib/r2/upload-chapter-image"
        );
        return uploadChapterImage(storyId, file);
    },
};
